// เนื้อเรื่องเฉพาะกลุ่มย่อยของสัตว์เลี้ยงลูกด้วยนม

import type { GameEvent } from "../../engine/types";

const M = ["MAMMAL"] as const;

export const MAMMAL_SUBGROUP_EVENTS: readonly GameEvent[] = [
  // ---------- วาฬและโลมา ----------
  {
    id: "whale_first_breath",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.whale"] },
    once: true,
    weight: 30,
    text: "คุณเกิดใต้น้ำ ปอดยังว่างเปล่า แม่ว่ายเข้ามาข้างๆ",
    fact: "ลูกวาฬและโลมาต้องขึ้นไปหายใจที่ผิวน้ำทันทีหลังเกิด และแม่มักช่วยดันลูกขึ้นไป",
    choices: [
      { label: "ให้แม่ดันขึ้นไปหายใจ", outcomes: [{ chance: 1, text: "คุณสูดอากาศเฮือกแรกเหนือผิวน้ำ", effects: { health: 10, safety: 10 } }] },
      {
        label: "พยายามว่ายขึ้นไปเอง",
        outcomes: [
          { chance: 0.8, text: "คุณขึ้นไปถึงผิวน้ำได้ด้วยตัวเอง", effects: { health: 5 } },
          { chance: 0.2, text: "คุณสำลักน้ำ แม่ต้องรีบเข้ามาช่วย", effects: { health: -15, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "whale_orca",
    archetypes: M,
    stages: ["nursing", "juvenile"],
    requires: { subgroups: ["mammal.whale"] },
    weight: 4,
    text: "ฝูงวาฬเพชฌฆาตกำลังว่ายวนเข้ามา พวกมันจ้องลูกวาฬตัวเล็กที่สุดในฝูง",
    choices: [
      {
        label: "ว่ายชิดแม่ไว้ให้แน่น",
        outcomes: [
          { chance: 0.8, text: "ฝูงผู้ใหญ่ล้อมคุณไว้ตรงกลาง วาฬเพชฌฆาตยอมแพ้ไป", effects: { safety: 10 } },
          { chance: 0.2, text: "คุณโดนกัดที่ครีบหาง แต่รอดมาได้", effects: { health: -25, addFlags: ["injured"], cause: "predator" } },
        ],
      },
      {
        label: "ว่ายหนีไปทางน้ำตื้น",
        outcomes: [
          { chance: 0.5, text: "น้ำตื้นทำให้พวกมันไล่ตามยาก คุณรอด", effects: { fullness: -10 } },
          { chance: 0.35, text: "คุณพลัดหลงจากฝูงอยู่ครึ่งวัน", effects: { safety: -20 } },
          { chance: 0.15, text: "คุณอยู่ตัวเดียว และพวกมันตามทัน", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "whale_pod_hunt",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.whale"] },
    weight: 8,
    text: "ฝูงของคุณเจอฝูงปลาขนาดใหญ่",
    fact: "โลมาหลายชนิดช่วยกันล้อมฝูงปลาให้รวมเป็นก้อน แล้วผลัดกันพุ่งเข้าไปกิน",
    choices: [
      { label: "ช่วยฝูงต้อนปลาให้รวมกัน", outcomes: [{ chance: 1, text: "ฝูงปลาจับกันเป็นก้อนกลม ทุกตัวได้กินอิ่ม", effects: { fullness: 40, safety: 5 } }] },
      {
        label: "พุ่งเข้าไปกินเลย",
        outcomes: [
          { chance: 0.6, text: "คุณได้ปลาไปหลายตัว", effects: { fullness: 30 } },
          { chance: 0.4, text: "ฝูงปลาแตกกระจาย ได้มาไม่กี่ตัว", effects: { fullness: 10 } },
        ],
      },
    ],
  },
  {
    id: "whale_ship",
    archetypes: M,
    stageTags: ["adult", "old"],
    requires: { subgroups: ["mammal.whale"] },
    weight: 2,
    threatenedWeight: 2,
    text: "เสียงใบพัดเรือดังกระหึ่มจนกลบเสียงเรียกของฝูง",
    choices: [
      {
        label: "ดำลงลึกให้พ้นทางเรือ",
        outcomes: [
          { chance: 0.85, text: "เรือแล่นผ่านไปเหนือหัวคุณ", effects: { fullness: -5 } },
          { chance: 0.15, text: "คุณขึ้นมาหายใจผิดจังหวะ ใบพัดเฉี่ยวหลัง", effects: { health: -30, cause: "human" } },
        ],
      },
      {
        label: "อยู่ที่ผิวน้ำต่อ",
        outcomes: [
          { chance: 0.6, text: "เรือเปลี่ยนเส้นทางไปทันเวลา" },
          { chance: 0.3, text: "ตัวเรือชนคุณจนบาดเจ็บสาหัส", effects: { health: -40, cause: "human" } },
          { chance: 0.1, text: "เรือลำใหญ่เกินกว่าจะหลบพ้น", effects: { death: "human" } },
        ],
      },
    ],
  },

  // ---------- ค้างคาว ----------
  {
    id: "bat_cling",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.bat"] },
    once: true,
    weight: 30,
    text: "คุณเกาะอยู่ที่อกแม่ ห้อยหัวลงจากเพดานถ้ำพร้อมค้างคาวอีกนับพัน",
    choices: [
      { label: "เกาะให้แน่นที่สุด", outcomes: [{ chance: 1, text: "แม่โอบปีกห่อตัวคุณไว้อย่างอบอุ่น", effects: { safety: 10, fullness: 10 } }] },
      {
        label: "ขยับไปดูรอบๆ",
        outcomes: [
          { chance: 0.7, text: "คุณเห็นลูกค้างคาวอีกนับร้อยเกาะอยู่รอบตัว" },
          { chance: 0.3, text: "คุณเกือบพลัดตกจากเพดานถ้ำ", effects: { health: -15, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "bat_nursery",
    archetypes: M,
    stages: ["nursing"],
    requires: { subgroups: ["mammal.bat"] },
    weight: 8,
    text: "แม่ออกไปหากินตอนกลางคืน ทิ้งคุณไว้ในถ้ำกับลูกค้างคาวตัวอื่นๆ",
    fact: "ค้างคาวแม่ลูกอ่อนจำเสียงร้องและกลิ่นของลูกตัวเองได้ แม้ในถ้ำจะมีลูกค้างคาวนับพันตัว",
    choices: [
      { label: "ร้องเรียกแม่เมื่อได้ยินเสียงปีก", outcomes: [{ chance: 1, text: "แม่บินตรงมาหาคุณท่ามกลางลูกค้างคาวนับพัน แล้วให้นม", effects: { fullness: 35 } }] },
      {
        label: "เงียบไว้ รอให้แม่หาเจอ",
        outcomes: [
          { chance: 0.6, text: "แม่ตามกลิ่นจนเจอคุณ", effects: { fullness: 30 } },
          { chance: 0.4, text: "แม่หาคุณเจอช้ากว่าปกติ คืนนี้คุณหิว", effects: { fullness: 5 } },
        ],
      },
    ],
  },
  {
    id: "bat_first_flight",
    opening: true,
    archetypes: M,
    stages: ["juvenile"],
    requires: { subgroups: ["mammal.bat"] },
    once: true,
    weight: 20,
    text: "ปีกของคุณแข็งแรงพอแล้ว คืนนี้ถึงเวลาบินออกจากถ้ำครั้งแรก",
    fact: "ค้างคาวกินแมลงส่งเสียงความถี่สูงแล้วฟังเสียงสะท้อน เพื่อรู้ตำแหน่งของแมลงในความมืด",
    choices: [
      { label: "บินตามฝูงออกไป", outcomes: [{ chance: 1, text: "ฝูงค้างคาวพุ่งออกจากปากถ้ำราวกับควันดำ คุณบินได้แล้ว!", effects: { safety: 10, fullness: 15 } }] },
      {
        label: "ลองฟังเสียงสะท้อนก่อนบิน",
        outcomes: [{ chance: 1, text: "คุณจับทิศทางในความมืดได้แม่นยำขึ้น", effects: { safety: 15 } }],
      },
    ],
  },
  {
    id: "bat_cave_visitors",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.bat"] },
    weight: 2,
    threatenedWeight: 2.5,
    text: "แสงไฟฉายสาดเข้ามาในถ้ำ มีคนเดินเข้ามาส่งเสียงดัง",
    choices: [
      { label: "ย้ายไปเกาะในซอกที่ลึกกว่า", outcomes: [{ chance: 1, text: "คุณหลบเข้าไปลึกจนแสงไฟส่องไม่ถึง", effects: { fullness: -10 } }] },
      {
        label: "บินหนีออกจากถ้ำกลางวัน",
        outcomes: [
          { chance: 0.6, text: "คุณหาที่เกาะใหม่ใต้ชายคาบ้านได้", effects: { safety: -15 } },
          { chance: 0.4, text: "แสงแดดจ้าทำให้คุณมึนงง นกล่าเหยื่อไล่ตาม", effects: { health: -20, cause: "predator" } },
        ],
      },
    ],
  },

  // ---------- สัตว์ฟันแทะ ----------
  {
    id: "rodent_nest",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.rodent"] },
    once: true,
    weight: 30,
    text: "คุณเกิดมาในรังใต้ดินพร้อมพี่น้องอีกหลายตัว ตายังไม่เปิด ตัวไม่มีขน",
    choices: [
      { label: "เบียดตัวกับพี่น้องให้อุ่น", outcomes: [{ chance: 1, text: "ไออุ่นจากพี่น้องช่วยให้คุณรอดคืนแรก", effects: { health: 10 } }] },
      {
        label: "คลานไปหาแม่",
        outcomes: [
          { chance: 0.7, text: "คุณเจอเต้านมก่อนใคร", effects: { fullness: 25 } },
          { chance: 0.3, text: "คุณคลานพลาดออกนอกรัง แม่ต้องคาบกลับมา", effects: { health: -5 } },
        ],
      },
    ],
  },
  {
    id: "rodent_gnaw",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.rodent"] },
    weight: 4,
    text: "ฟันหน้าของคุณยาวขึ้นเรื่อยๆ จนรู้สึกคันเหงือก",
    fact: "ฟันหน้าของสัตว์ฟันแทะงอกยาวตลอดชีวิต จึงต้องแทะของแข็งอยู่เสมอเพื่อไม่ให้ฟันยาวเกินไป",
    choices: [
      { label: "แทะกิ่งไม้แข็งๆ", outcomes: [{ chance: 1, text: "ฟันสึกพอดี กลับมากินอาหารได้คล่อง", effects: { health: 10, fullness: 5 } }] },
      {
        label: "แทะสายไฟของมนุษย์",
        outcomes: [
          { chance: 0.7, text: "เคี้ยวเพลินดี แต่บ้านคนข้างบนไฟดับ", effects: { safety: -10 } },
          { chance: 0.3, text: "ไฟดูดคุณจนสลบไปพักใหญ่", effects: { health: -35, cause: "human" } },
        ],
      },
    ],
  },
  {
    id: "rodent_hoard",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.rodent"] },
    weight: 7,
    text: "คุณเจอเมล็ดพืชหล่นอยู่เต็มพื้น",
    choices: [
      { label: "ขนกลับไปเก็บสะสมในรัง", outcomes: [{ chance: 1, text: "คลังอาหารของคุณเต็ม พร้อมสำหรับวันที่อาหารขาดแคลน", effects: { fullness: 20, addFlags: ["food_store"] } }] },
      {
        label: "นั่งกินตรงนั้นเลย",
        outcomes: [
          { chance: 0.7, text: "อร่อยและอิ่มทันที", effects: { fullness: 35 } },
          { chance: 0.3, text: "นกเค้าแมวโฉบลงมาจากความมืด คุณหนีกลับรูทัน", effects: { fullness: 15, safety: -20 } },
        ],
      },
    ],
  },
  {
    id: "rodent_store_used",
    archetypes: M,
    stageTags: ["adult", "old"],
    requires: { subgroups: ["mammal.rodent"], flags: ["food_store"] },
    weight: 5,
    text: "ข้างนอกฝนตกหนักหลายวัน ออกไปหาอาหารไม่ได้",
    choices: [{ label: "กินอาหารที่สะสมไว้", outcomes: [{ chance: 1, text: "คลังอาหารที่เก็บไว้ช่วยชีวิตคุณ", effects: { fullness: 35, removeFlags: ["food_store"] } }] }],
  },
  {
    id: "rodent_snake",
    archetypes: M,
    stages: ["nursing", "juvenile", "adult"],
    requires: { subgroups: ["mammal.rodent"] },
    weight: 4,
    text: "งูตัวหนึ่งเลื้อยเข้ามาในรู",
    choices: [
      {
        label: "หนีออกทางรูลับ",
        outcomes: [
          { chance: 0.8, text: "รูที่คุณขุดไว้หลายทางช่วยให้หนีรอด", effects: { safety: -10 } },
          { chance: 0.2, text: "รูลับถูกดินถล่มปิดไว้", effects: { death: "predator" } },
        ],
      },
      {
        label: "ขุดดินปิดทางให้แน่น",
        outcomes: [
          { chance: 0.5, text: "งูหาทางเข้าไม่ได้และเลื้อยจากไป", effects: { safety: 5 } },
          { chance: 0.5, text: "งูเร็วกว่าที่คิด คุณโดนกัดแต่ดิ้นหลุด", effects: { health: -30, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "rodent_quick_family",
    archetypes: M,
    stages: ["adult"],
    requires: { subgroups: ["mammal.rodent"], notFlags: ["has_mate"] },
    weight: 8,
    text: "ชีวิตของคุณสั้น ทุกฤดูมีค่า มีตัวอื่นส่งกลิ่นเรียกอยู่ใกล้ๆ",
    choices: [
      { label: "ตามกลิ่นไป", outcomes: [{ chance: 1, text: "คุณเจอคู่ภายในคืนเดียว", effects: { addFlags: ["has_mate"], fullness: -5 } }] },
      { label: "หาอาหารก่อน", outcomes: [{ chance: 1, text: "คุณกินจนอิ่ม แต่โอกาสก็หลุดลอยไป", effects: { fullness: 20 } }] },
    ],
  },

  // ---------- สัตว์กินเนื้อ ----------
  {
    id: "carn_den",
    archetypes: M,
    stages: ["newborn", "nursing"],
    requires: { subgroups: ["mammal.carnivore"] },
    weight: 6,
    text: "แม่ซ่อนคุณกับพี่น้องไว้ในโพรงใต้รากไม้ใหญ่",
    choices: [
      { label: "นอนนิ่งรอแม่กลับมา", outcomes: [{ chance: 1, text: "แม่กลับมาพร้อมเหยื่อ และย้ายรังไปที่ใหม่เพื่อความปลอดภัย", effects: { safety: 10, fullness: 15 } }] },
      {
        label: "คลานออกไปเล่นหน้าโพรง",
        outcomes: [
          { chance: 0.7, text: "ข้างนอกมีผีเสื้อบินผ่าน คุณตะครุบพลาดไปหลายรอบ" },
          { chance: 0.3, text: "นกอินทรีบินวนอยู่เหนือหัว คุณรีบมุดกลับเข้าโพรง", effects: { safety: -15 } },
        ],
      },
    ],
  },
  {
    id: "carn_hunt_lesson",
    opening: true,
    archetypes: M,
    stages: ["juvenile"],
    requires: { subgroups: ["mammal.carnivore"] },
    once: true,
    weight: 15,
    text: "แม่พาคุณออกล่าเป็นครั้งแรก",
    choices: [
      {
        label: "ดูแม่ล่าก่อน",
        outcomes: [{ chance: 1, text: "คุณเรียนรู้ว่าต้องเข้าใกล้เหยื่อจากทางใต้ลม", effects: { fullness: 20, addFlags: ["skilled_hunter"] } }],
      },
      {
        label: "พุ่งออกไปเอง",
        outcomes: [
          { chance: 0.4, text: "คุณจับเหยื่อตัวแรกได้ด้วยตัวเอง!", effects: { fullness: 30, addFlags: ["skilled_hunter"] } },
          { chance: 0.6, text: "เหยื่อหนีไปหมด แม่มองคุณอย่างอ่อนใจ", effects: { fullness: -5 } },
        ],
      },
    ],
  },
  {
    id: "carn_skilled_hunt",
    archetypes: M,
    stages: ["adult", "elder"],
    requires: { subgroups: ["mammal.carnivore"], flags: ["skilled_hunter"] },
    weight: 5,
    text: "ประสบการณ์บอกคุณว่าเหยื่อจะเดินผ่านเส้นทางนี้",
    choices: [{ label: "ซุ่มรอที่จุดเดิม", outcomes: [{ chance: 1, text: "เป็นไปตามที่คาด คุณได้อาหารมื้อใหญ่", effects: { fullness: 45 } }] }],
  },
  {
    id: "carn_scent_mark",
    archetypes: M,
    stages: ["adult"],
    requires: { subgroups: ["mammal.carnivore"] },
    weight: 4,
    text: "คุณเดินตรวจอาณาเขต กลิ่นของคุณที่ทิ้งไว้เริ่มจางลง",
    choices: [
      { label: "ทำเครื่องหมายใหม่ทุกต้นไม้", outcomes: [{ chance: 1, text: "ตัวอื่นรู้ว่าที่นี่มีเจ้าของ", effects: { safety: 15, fullness: -10 } }] },
      {
        label: "ปล่อยไว้ก่อน",
        outcomes: [
          { chance: 0.6, text: "ไม่มีใครเข้ามา" },
          { chance: 0.4, text: "มีตัวอื่นเริ่มย่องเข้ามาล่าในเขตของคุณ", effects: { safety: -15, fullness: -10 } },
        ],
      },
    ],
  },
  {
    id: "carn_livestock",
    archetypes: M,
    stages: ["adult", "elder"],
    requires: { subgroups: ["mammal.carnivore"] },
    weight: 2,
    threatenedWeight: 2.5,
    text: "เหยื่อในป่าหายากขึ้น คุณได้กลิ่นวัวในคอกของหมู่บ้านใกล้ๆ",
    fact: "การที่ป่าหดเล็กลงทำให้สัตว์ผู้ล่าหลายชนิดต้องเข้าใกล้หมู่บ้าน และเกิดความขัดแย้งกับคนมากขึ้น",
    choices: [
      {
        label: "ลอบเข้าไปกินวัว",
        outcomes: [
          { chance: 0.5, text: "คุณได้อาหารแล้วรีบกลับเข้าป่า", effects: { fullness: 45, safety: -20 } },
          { chance: 0.4, text: "ชาวบ้านไล่ตามจนคุณบาดเจ็บ", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "มีกับดักรออยู่ที่คอก", effects: { death: "human" } },
        ],
      },
      { label: "อดทนล่าในป่าต่อ", outcomes: [{ chance: 1, text: "คุณได้เหยื่อตัวเล็กๆ พอประทังชีวิต", effects: { fullness: 15 } }] },
    ],
  },
  {
    id: "carn_scavenge",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.carnivore"] },
    weight: 3,
    text: "คุณเจอซากสัตว์ที่ตัวอื่นล่าทิ้งไว้ มีแร้งเริ่มรุมกิน",
    choices: [
      { label: "ไล่แร้งแล้วกินเอง", outcomes: [{ chance: 1, text: "อาหารฟรีโดยไม่ต้องล่าเอง", effects: { fullness: 30 } }] },
      {
        label: "ระวังเจ้าของซากจะกลับมา",
        outcomes: [
          { chance: 0.5, text: "เจ้าของซากกลับมาจริงๆ ดีที่คุณถอยออกมาก่อน", effects: { safety: 10 } },
          { chance: 0.5, text: "ไม่มีใครกลับมา คุณเสียโอกาสไปฟรีๆ", effects: { fullness: -5 } },
        ],
      },
    ],
  },

  // ---------- สัตว์กีบ ----------
  {
    id: "ung_stand_up",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.ungulate"] },
    once: true,
    weight: 30,
    text: "คุณเพิ่งเกิดได้ไม่กี่นาที แม่เลียตัวคุณและดันให้ลุกขึ้น",
    fact: "ลูกสัตว์กีบหลายชนิดยืนและเดินตามแม่ได้ภายในไม่กี่ชั่วโมงหลังเกิด เพื่อหนีนักล่าให้ทัน",
    choices: [
      {
        label: "พยายามลุกขึ้นยืน",
        outcomes: [
          { chance: 0.7, text: "ขาของคุณสั่นเทา แต่คุณยืนได้แล้ว!", effects: { safety: 10 } },
          { chance: 0.3, text: "คุณล้มลงหลายครั้งกว่าจะยืนได้", effects: { health: -5, safety: 5 } },
        ],
      },
      { label: "นอนพักอีกนิด", outcomes: [{ chance: 1, text: "แม่ยืนเฝ้าคุณอย่างกระวนกระวาย", effects: { safety: -10, health: 5 } }] },
    ],
  },
  {
    id: "ung_hide_grass",
    archetypes: M,
    stages: ["newborn", "nursing"],
    requires: { subgroups: ["mammal.ungulate"] },
    weight: 6,
    text: "แม่ซ่อนคุณไว้ในพงหญ้าสูง แล้วออกไปกินหญ้าห่างๆ",
    fact: "ลูกกวางหลายชนิดแทบไม่มีกลิ่นตัว และจะหมอบนิ่งในพงหญ้าขณะแม่ออกไปหากิน",
    choices: [
      {
        label: "หมอบนิ่งไม่ขยับ",
        outcomes: [
          { chance: 0.85, text: "สุนัขป่าเดินผ่านไปโดยไม่ได้กลิ่นคุณ", effects: { safety: 10 } },
          { chance: 0.15, text: "นักล่าเกือบเหยียบคุณ แม่วิ่งมาไล่มันไปทัน", effects: { safety: -15 } },
        ],
      },
      {
        label: "ลุกขึ้นวิ่งไปหาแม่",
        outcomes: [
          { chance: 0.6, text: "คุณถึงตัวแม่อย่างปลอดภัย", effects: { fullness: 15 } },
          { chance: 0.3, text: "การเคลื่อนไหวของคุณดึงดูดนักล่า", effects: { safety: -20 } },
          { chance: 0.1, text: "นักล่าวิ่งเร็วกว่าขาเล็กๆ ของคุณ", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "ung_herd_alarm",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.ungulate"] },
    weight: 6,
    text: "ตัวหนึ่งในฝูงส่งเสียงเตือนภัย ทั้งฝูงเงยหน้าขึ้นพร้อมกัน",
    choices: [
      { label: "วิ่งตามฝูงทันที", outcomes: [{ chance: 1, text: "ฝูงวิ่งหนีไปพร้อมกัน นักล่าไม่รู้จะเลือกตัวไหน", effects: { safety: 15, fullness: -10 } }] },
      {
        label: "หยุดดูว่าภัยอยู่ทางไหน",
        outcomes: [
          { chance: 0.6, text: "แค่สัญญาณหลอก คุณกินหญ้าต่อได้", effects: { fullness: 15 } },
          { chance: 0.3, text: "เสือกำลังพุ่งออกมาจากพุ่มไม้ คุณหนีทันอย่างหวุดหวิด", effects: { health: -15, safety: -15, cause: "predator" } },
          { chance: 0.1, text: "คุณหยุดนานเกินไป", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "ung_rut",
    archetypes: M,
    stages: ["adult"],
    requires: { subgroups: ["mammal.ungulate"], notFlags: ["has_mate"] },
    weight: 7,
    text: "ฤดูผสมพันธุ์มาถึง ตัวผู้ในฝูงเริ่มประลองกำลังกัน",
    choices: [
      {
        label: "ลงประลองหรือเลือกผู้ชนะ",
        outcomes: [
          { chance: 0.6, text: "การประลองจบลง และคุณได้คู่", effects: { addFlags: ["has_mate"], fullness: -10 } },
          { chance: 0.4, text: "เขาปะทะกันอย่างแรง คุณบาดเจ็บ", effects: { health: -20, addFlags: ["injured"], cause: "accident" } },
        ],
      },
      { label: "อยู่ห่างๆ ปีนี้ไม่ยุ่ง", outcomes: [{ chance: 1, text: "คุณกินหญ้าอย่างสงบขณะตัวอื่นวุ่นวาย", effects: { fullness: 15 } }] },
    ],
  },
  {
    id: "ung_river",
    archetypes: M,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["mammal.ungulate"] },
    weight: 3,
    text: "ฝูงต้องข้ามแม่น้ำเชี่ยวเพื่อไปยังทุ่งหญ้าอีกฝั่ง",
    choices: [
      {
        label: "ข้ามไปกลางฝูง",
        outcomes: [
          { chance: 0.85, text: "คุณข้ามไปถึงทุ่งหญ้าเขียวขจีอีกฝั่ง", effects: { fullness: 25 } },
          { chance: 0.15, text: "กระแสน้ำพัดคุณออกนอกฝูง กว่าจะขึ้นฝั่งได้ก็หมดแรง", effects: { health: -20, cause: "accident" } },
        ],
      },
      {
        label: "รอข้ามเป็นตัวสุดท้าย",
        outcomes: [
          { chance: 0.6, text: "น้ำนิ่งลงแล้ว คุณข้ามได้สบาย", effects: { fullness: 15 } },
          { chance: 0.3, text: "จระเข้รอตัวที่ข้ามช้าอยู่ คุณสะบัดหลุดมาได้", effects: { health: -30, cause: "predator" } },
          { chance: 0.1, text: "จระเข้ลากคุณลงใต้น้ำ", effects: { death: "predator" } },
        ],
      },
    ],
  },

  // ---------- ไพรเมต ----------
  {
    id: "pri_cling",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.primate"] },
    once: true,
    weight: 30,
    text: "คุณกำขนที่ท้องแม่ไว้แน่น ขณะแม่ปีนต้นไม้สูงขึ้นไปเรื่อยๆ",
    choices: [
      { label: "เกาะไม่ปล่อย", outcomes: [{ chance: 1, text: "มือเล็กๆ ของคุณแข็งแรงกว่าที่คิด", effects: { safety: 10 } }] },
      {
        label: "ยื่นมือคว้ากิ่งไม้",
        outcomes: [
          { chance: 0.6, text: "แม่ยิ้ม และให้คุณลองจับกิ่งไม้ใกล้ๆ", effects: { health: 5 } },
          { chance: 0.4, text: "คุณหลุดมือ แม่คว้าไว้ทัน", effects: { safety: -10 } },
        ],
      },
    ],
  },
  {
    id: "pri_grooming",
    archetypes: M,
    stages: ["nursing", "juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.primate"] },
    weight: 6,
    text: "ตัวหนึ่งในฝูงนั่งลงข้างๆ และเริ่มไซ้ขนให้คุณ",
    fact: "ลิงใช้การไซ้ขนกำจัดเห็บเหา และสร้างความสัมพันธ์กับตัวอื่นในฝูงไปพร้อมกัน",
    choices: [
      { label: "ไซ้ขนให้มันกลับ", outcomes: [{ chance: 1, text: "คุณได้เพื่อนในฝูงเพิ่มอีกตัว", effects: { safety: 15, health: 5, addFlags: ["troop_friend"] } }] },
      { label: "นอนให้ไซ้อย่างเดียว", outcomes: [{ chance: 1, text: "สบายตัวดี เห็บเหาหายไปเยอะ", effects: { health: 10 } }] },
    ],
  },
  {
    id: "pri_friend_helps",
    archetypes: M,
    stageTags: ["young", "adult", "old"],
    requires: { subgroups: ["mammal.primate"], flags: ["troop_friend"] },
    weight: 3,
    text: "ตัวที่ใหญ่กว่ากำลังจะแย่งอาหารของคุณ",
    choices: [{ label: "มองหาเพื่อนในฝูง", outcomes: [{ chance: 1, text: "เพื่อนที่คุณเคยไซ้ขนให้เข้ามายืนข้างคุณ ตัวนั้นยอมถอยไป", effects: { fullness: 20, safety: 10 } }] }],
  },
  {
    id: "pri_tools",
    archetypes: M,
    stages: ["juvenile", "adult"],
    requires: { subgroups: ["mammal.primate"] },
    once: true,
    weight: 6,
    text: "คุณเห็นตัวโตเต็มวัยใช้หินทุบเปลือกหอยแข็งๆ",
    fact: "ลิงแสมตามชายฝั่งทะเลของไทยใช้ก้อนหินทุบเปลือกหอยและปู เป็นตัวอย่างการใช้เครื่องมือของสัตว์",
    choices: [
      {
        label: "หยิบหินมาลองบ้าง",
        outcomes: [
          { chance: 0.5, text: "คุณทุบเปลือกแตกได้ในที่สุด!", effects: { fullness: 25, addFlags: ["uses_tools"] } },
          { chance: 0.5, text: "หินหล่นทับนิ้วตัวเอง", effects: { health: -10, cause: "accident" } },
        ],
      },
      { label: "รอเก็บเศษที่ตัวอื่นกินเหลือ", outcomes: [{ chance: 1, text: "ได้มานิดหน่อย", effects: { fullness: 10 } }] },
    ],
  },
  {
    id: "pri_tools_payoff",
    archetypes: M,
    stages: ["adult", "elder"],
    requires: { subgroups: ["mammal.primate"], flags: ["uses_tools"] },
    weight: 4,
    text: "น้ำทะเลลดลง ก้อนหินเต็มไปด้วยหอยนางรม",
    choices: [{ label: "ใช้หินทุบเปลือกอย่างชำนาญ", outcomes: [{ chance: 1, text: "ทักษะที่ฝึกไว้ทำให้คุณกินได้มากกว่าตัวอื่น", effects: { fullness: 40 } }] }],
  },
  {
    id: "pri_tourist_food",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.primate"] },
    weight: 3,
    text: "นักท่องเที่ยวยื่นขนมถุงหวานๆ มาให้",
    choices: [
      {
        label: "คว้ามากินเลย",
        outcomes: [
          { chance: 0.6, text: "หวานอร่อย แต่ไม่ใช่อาหารที่ร่างกายต้องการ", effects: { fullness: 20, health: -5 } },
          { chance: 0.4, text: "กินบ่อยจนท้องเสีย", effects: { fullness: 10, health: -20, cause: "disease" } },
        ],
      },
      { label: "กลับไปหาผลไม้ในป่า", outcomes: [{ chance: 1, text: "อาหารธรรมชาติทำให้คุณแข็งแรง", effects: { fullness: 20, health: 5 } }] },
    ],
  },

  // ---------- สัตว์มีกระเป๋าหน้าท้อง ----------
  {
    id: "mar_pouch_climb",
    opening: true,
    archetypes: M,
    stages: ["newborn"],
    requires: { subgroups: ["mammal.marsupial"] },
    once: true,
    weight: 30,
    text: "คุณเกิดมาตัวเล็กเท่าเมล็ดถั่ว ต้องคลานไปตามขนของแม่ให้ถึงกระเป๋าหน้าท้อง",
    fact: "ลูกจิงโจ้แรกเกิดมีขนาดเล็กมาก และต้องคลานเข้าไปในกระเป๋าหน้าท้องแม่ด้วยตัวเอง",
    choices: [
      {
        label: "คลานต่อไปไม่หยุด",
        outcomes: [
          { chance: 0.9, text: "คุณถึงกระเป๋าหน้าท้อง และงับหัวนมไว้แน่น", effects: { fullness: 30, safety: 15 } },
          { chance: 0.1, text: "คุณหมดแรงกลางทาง", effects: { death: "accident" } },
        ],
      },
    ],
  },
  {
    id: "mar_pouch_life",
    archetypes: M,
    stages: ["nursing"],
    requires: { subgroups: ["mammal.marsupial"] },
    weight: 8,
    text: "คุณอยู่ในกระเป๋าหน้าท้องที่อบอุ่น แม่กระโดดไปมาทั้งวัน",
    choices: [
      { label: "ดูดนมแล้วหลับ", outcomes: [{ chance: 1, text: "คุณโตขึ้นทุกวันในที่ที่ปลอดภัยที่สุด", effects: { fullness: 30, health: 5 } }] },
      { label: "โผล่หัวออกไปดูโลก", outcomes: [{ chance: 1, text: "ข้างนอกกว้างใหญ่และน่าตื่นเต้น", effects: { safety: -5, fullness: 15 } }] },
    ],
  },
  {
    id: "mar_bushfire",
    archetypes: M,
    stageTags: ["young", "adult", "old"],
    requires: { subgroups: ["mammal.marsupial"], habitat: ["forest", "grassland"] },
    weight: 2,
    text: "กลิ่นควันลอยมาแรงขึ้นเรื่อยๆ ไฟป่ากำลังลุกลามมา",
    choices: [
      {
        label: "หนีไปทางลำธาร",
        outcomes: [
          { chance: 0.75, text: "คุณหลบอยู่ในน้ำจนไฟผ่านไป", effects: { fullness: -15 } },
          { chance: 0.25, text: "ควันหนาจนคุณหายใจไม่ออก", effects: { health: -30, cause: "disaster" } },
        ],
      },
      {
        label: "ปีนขึ้นต้นไม้สูง",
        outcomes: [
          { chance: 0.5, text: "ไฟผ่านไปด้านล่าง คุณรอดมาได้", effects: { health: -10, cause: "disaster" } },
          { chance: 0.4, text: "ความร้อนแผดเผาจนขนไหม้", effects: { health: -35, cause: "disaster" } },
          { chance: 0.1, text: "ไฟลามขึ้นถึงยอดไม้", effects: { death: "disaster" } },
        ],
      },
    ],
  },

  // ---------- สัตว์เลี้ยงลูกด้วยนมอื่นๆ (ช้าง ตัวนิ่ม เม่น กระต่าย พะยูน ฯลฯ) ----------
  {
    id: "other_mud_bath",
    archetypes: M,
    stages: ["juvenile", "adult", "elder"],
    requires: { subgroups: ["mammal.other"] },
    weight: 5,
    text: "แดดร้อนจัด มีปลักโคลนเย็นๆ อยู่ไม่ไกล",
    fact: "ช้างอาบโคลนและพ่นฝุ่นใส่ตัว เพื่อช่วยกันแดดและไล่แมลง",
    choices: [
      { label: "ลงไปเกลือกโคลน", outcomes: [{ chance: 1, text: "โคลนเย็นสบาย ช่วยกันแดดและแมลงกัด", effects: { health: 10 } }] },
      { label: "หาร่มไม้นอนแทน", outcomes: [{ chance: 1, text: "ร่มไม้ช่วยได้บ้าง แต่แมลงก็ตอมไม่หยุด", effects: { health: 3 } }] },
    ],
  },
  {
    id: "other_snare",
    archetypes: M,
    stageTags: ["young", "adult", "old"],
    requires: { subgroups: ["mammal.other", "mammal.ungulate", "mammal.carnivore", "mammal.primate"], habitat: ["forest"] },
    weight: 1.5,
    threatenedWeight: 3,
    text: "เส้นทางที่คุณเดินประจำ มีบ่วงลวดซ่อนอยู่ใต้ใบไม้",
    fact: "บ่วงดักสัตว์เป็นภัยคุกคามสำคัญของสัตว์ป่าในเอเชียตะวันออกเฉียงใต้",
    choices: [
      {
        label: "เดินทางเดิมต่อไป",
        outcomes: [
          { chance: 0.6, text: "คุณก้าวข้ามบ่วงไปโดยบังเอิญ" },
          { chance: 0.3, text: "บ่วงรัดขาคุณ คุณดิ้นจนหลุดแต่แผลลึก", effects: { health: -35, addFlags: ["injured"], cause: "human" } },
          { chance: 0.1, text: "บ่วงรัดแน่นจนดิ้นไม่หลุด", effects: { death: "human" } },
        ],
      },
      { label: "รู้สึกแปลกๆ เลี่ยงไปทางอื่น", outcomes: [{ chance: 1, text: "สัญชาตญาณช่วยคุณไว้", effects: { fullness: -5, safety: 5 } }] },
    ],
  },
];
